import React, {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
} from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { numberWithCommas } from 'shared/utils/numberUtils';
import styled from 'styled-components';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/pro-light-svg-icons';
import { IndicatorDetails, IndicatorDetailSectionProps } from '../utils';
import { IndicatorDetailsHandler } from '../../../../NPOFeedback/FeedbackDetails/ImpactDetailsEdit/utils';
import {
  getForecastSentence,
  getIndicatorSentence,
} from '../../ImpactIndicator/IndicatorMadLibs';
import { ForecastSupportText } from '../../ImpactIndicator/SupportText';
import { getReachIndicator } from './utils';
import { useReport } from 'shared/providers/ReportProvider';

const getIndicatorForecast = (
  indicatorDetails: IndicatorDetails,
  isReportPassed: boolean,
): IndicatorDetails['forecast'] => {
  const forecastValue = isReportPassed
    ? indicatorDetails.finalForecast
    : indicatorDetails.forecast;
  return forecastValue ? numberWithCommas(forecastValue) : '';
};

const StyledDiv = styled.div`
  .forecast-heading {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
  }
  .support-text {
    margin-bottom: ${SPACERS.medium};
  }
  .input-container {
    position: relative;
    max-width: 446px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.large};
    .great {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      background-color: ${COLORS.grayLightest};
      svg {
        margin-bottom: ${SPACERS.tiny};
        path {
          fill: ${COLORS.success};
        }
      }
    }
    input {
      width: 100%;
      padding: 12px;
      text-align: center;
      font-weight: 700;
      font-size: 72px;
      line-height: 98px;
      letter-spacing: -0.02em;
      border-radius: 4px 4px 0px 0px;
      background-color: ${COLORS.grayLight};
      border: 1px solid ${COLORS.ctaPrimary};
      -moz-appearance: textfield;
    }
    input:disabled {
      color: ${COLORS.greyBase};
    }
  }
`;

const validateForecastNext = (forecast: IndicatorDetails['forecast']) =>
  Boolean(forecast);

const IndicatorForecast = React.forwardRef<
  IndicatorDetailsHandler,
  IndicatorDetailSectionProps & {
    indicatorDetails: IndicatorDetails;
  }
>(
  (
    {
      indicator,
      className,
      hideBottomButtons,
      requestState,
      indicatorDetails,
      selectedSectionKey,
      beneficiaryTerm,
      allIndicators,
      reportPeriod,
      programName,
      indicators,
      intermediaries,
      serviceGoal,
      serviceType,
      selectedIndex,
      setIndicators,
      handleIndicatorSave,
      onNext,
      onPrevious,
    },
    forwardRef,
  ) => {
    const { isReportEndDatePassed } = useReport();
    const reachHumanIndicator = getReachIndicator(indicators.reach);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [cursor, setCursor] = useState<number | null>(null);
    const [forecast, setForecast] = useState<IndicatorDetails['forecast']>(
      getIndicatorForecast(indicatorDetails, isReportEndDatePassed),
    );

    useEffect(() => {
      setForecast(getIndicatorForecast(indicatorDetails, isReportEndDatePassed));
    }, [indicatorDetails]);

    useImperativeHandle(forwardRef, () => ({
      answer: () => ({
        answer: forecast,
        errorText: validateForecastNext(forecast)
          ? undefined
          : 'Please add a valid forecast.',
      }),
    }));

    useEffect(() => {
      cursor && inputRef.current?.setSelectionRange(cursor, cursor);
    }, [inputRef.current, cursor, forecast]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, selectionStart } = e.target;
        const newValue = numberWithCommas(
          parseInt(value.replace(/,/gi, '')) || '',
        );

        setForecast(newValue);
        setCursor(
          value.length === newValue.length - 1
            ? (selectionStart || 0) + 1
            : selectionStart,
        );
      },
      [setForecast, setCursor],
    );

    const handleNext = useCallback(() => {
      onNext(
        isReportEndDatePassed ? { finalForecast: forecast } : { forecast },
      );
      setIndicators(s => {
        const newState = { ...s };
        if (isReportEndDatePassed) {
          newState[selectedSectionKey][selectedIndex].finalForecast = forecast;
        } else {
          newState[selectedSectionKey][selectedIndex].forecast = forecast;
        }
        return newState;
      });
      const newIndicatorDetails = { ...indicatorDetails };
      if (isReportEndDatePassed) {
        newIndicatorDetails.finalForecast = forecast;
      } else {
        newIndicatorDetails.forecast = forecast;
      }
      handleIndicatorSave(newIndicatorDetails);
    }, [
      forecast,
      isReportEndDatePassed,
      selectedIndex,
      indicatorDetails,
      selectedSectionKey,
      onNext,
      setIndicators,
      handleIndicatorSave,
    ]);

    const sentence =
      getForecastSentence(
        allIndicators[indicatorDetails.indicatorId],
        programName,
        beneficiaryTerm,
        intermediaries,
        isReportEndDatePassed,
        reportPeriod,
        serviceGoal,
        serviceType,
        selectedSectionKey,
        reachHumanIndicator,
        indicators.learn[0],
        indicators.act[0],
      ) ||
      getIndicatorSentence(
        indicator || { id: indicatorDetails.indicatorId },
        isReportEndDatePassed ? 'ForecastFinal' : 'ForecastInitial',
        indicatorDetails.indicatorName,
        selectedSectionKey,
        indicatorDetails.forecast,
        beneficiaryTerm,
      );

    return (
      <StyledDiv className={className}>
        <TrueImpactText variant="heading-page" className="forecast-heading">
          {sentence}
        </TrueImpactText>
        <ForecastSupportText
          isReportPassed={isReportEndDatePassed}
          sectionKey={selectedSectionKey}
          className="support-text"
        />
        <TrueImpactText className="forecast-subheading">
          Enter your{' '}
          <strong>{isReportEndDatePassed ? 'results' : 'forecast'}</strong>{' '}
          below:
          {isReportEndDatePassed && indicatorDetails.forecast && (
            <>
              {' '}
              (initial forecast:{' '}
              <strong>{numberWithCommas(indicatorDetails.forecast)}</strong>)
            </>
          )}
        </TrueImpactText>
        <div className="input-container">
          <input
            key={indicatorDetails.indicatorId}
            ref={inputRef}
            disabled={requestState === 'saving' || requestState === 'saved'}
            type="text"
            value={forecast}
            autoFocus={true}
            onChange={handleChange}
          />
          {requestState === 'saved' && (
            <div className="great">
              <FontAwesomeIcon icon={faThumbsUp} size="2x" />
              <TrueImpactText variant="body-bold">Great!</TrueImpactText>
            </div>
          )}
        </div>
        {!hideBottomButtons && (
          <IndicatorBottomButtons
            requestState={requestState}
            disabledNext={!validateForecastNext(forecast)}
            onNext={handleNext}
            onPrevious={onPrevious}
          />
        )}
      </StyledDiv>
    );
  },
);

export default IndicatorForecast;
